import { useRef, useEffect } from 'react';

type Threshold = 25 | 50 | 75 | 95;
interface PlayerRef {
  current?: {
    getPosition: () => number | null;
    getDuration: () => number | null;
  };
}

interface LastPlaybackRef {
  current: {
    position: number;
    duration: number;
  };
}

interface ProgressResult {
  position: number | null;
  shouldSend: boolean;
  threshold?: Threshold;
}

export const useVideoProgressThresholds = (playerRef: PlayerRef, lastPlaybackRef: LastPlaybackRef, isLiveContent: boolean, mediaId?: string | number) => {
  const thresholdsRef = useRef<Record<Threshold, boolean>>({
    '25': false,
    '50': false,
    '75': false,
    '95': false,
  });

  // Reset the thresholds whenever a new video loads
  useEffect(() => {
    thresholdsRef.current = {
      '25': false,
      '50': false,
      '75': false,
      '95': false,
    };
  }, [mediaId]);

  // Threshold checker
  const checkThreshold = (progress: number): Threshold | null => {
    if (progress >= 95) { return 95; }
    if (progress >= 75) { return 75; }
    if (progress >= 50) { return 50; }
    if (progress >= 25) { return 25; }
    return null;
  };

  const getProgressData = (): ProgressResult => {
    // --- PLAYER POSITION ---
    const rawPos = lastPlaybackRef.current.position ?? 0;

    // --- PLAYER DURATION ---
    const rawDur = lastPlaybackRef.current.duration ?? 0;

    // Progress percent
    const progressPercent = rawDur > 0 ? +((rawPos / rawDur) * 100).toFixed(2) : 0;

    if (isLiveContent) {
      return { position: null, shouldSend: false };
    }

    const threshold = checkThreshold(progressPercent);
    if (!threshold) { return { position: null, shouldSend: false }; }

    // Already triggered before?
    if (thresholdsRef.current[threshold]) {
      return { position: null, shouldSend: false };
    }

    // Mark threshold as hit
    thresholdsRef.current[String(threshold) as '25' | '50' | '75' | '95'] = true;

    return {
      position: rawPos,
      shouldSend: true,
      threshold,
    };
  };

  return { getProgressData };
};
